import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import { FunctionCoach } from "../../../../../../../../api/services/program/models";
import { ProjectParticipantActivityParams } from "../../../../../../../../api/services/project/model";
import {
  useGetActivityTypesListByProjectId,
  useGetTopicListByProjectId,
  useSaveActivityForProjectMutation,
  useUpdateActivityForProjectMutation,
} from "../../../../../../../../api/services/project/projectApis";
import { MuiModal, OptionsDataType } from "../../../../../../../../components";
import { ActivityForm } from "../../../../../../../../components/activityForm/ActivityForm";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { ActivityTypeListItem } from "../../../../../../../viewProject/modelsAndSchemas/models";
import {
  AddActivityDefaultFormValue,
  AddActivityFormProps,
  AddProjectActivityFormKey,
  ProjectActivityResponse,
} from "../../../../../../modelsAndSchemas/Models";
import { AddActivitySchema } from "../../../../../../modelsAndSchemas/ValidationSchema";
import { activityTimelineaddActivitySiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { AddActivityWrapper } from "../../../../CreateProjectTabs.style";

interface AddActivityProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  projectId: string;
  getListOfActivitiesApi: () => void;
  isEditActivity?: boolean;
  editActivityData?: ProjectActivityResponse;
  projectCoachList?: FunctionCoach[];
}

export const AddActivity: FC<AddActivityProps> = ({
  show,
  handleCloseModal,
  pageId,
  projectId,
  getListOfActivitiesApi,
  isEditActivity = false,
  editActivityData,
  projectCoachList,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
    commonValidationMessages,
    fetchComponentTitle,
  } = useSiteCore();

  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const [filteredModality, setFilteredModality] = useState<OptionsDataType[]>(
    [],
  );

  const topicRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const [showCancel, setShowCancel] = useState<boolean>(false);
  const [isEffectExecuted, setIsEffectExecuted] = useState<boolean>(false);

  const getLabel = (key: string) => {
    return fetchLabel(
      activityTimelineaddActivitySiteCoreKeys.activityTimeline,
      key,
    );
  };

  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const methods = useForm<AddActivityFormProps>({
    mode: "all",
    defaultValues: AddActivityDefaultFormValue,
    resolver: yupResolver(AddActivitySchema(commonValidationMessages)),
  });

  const {
    getValues,
    setValue,
    reset,
    formState: { isDirty, dirtyFields },
    handleSubmit,
    control,
  } = methods;

  useEffect(() => {
    if (projectId) {
      getActivityList(projectId);
    }
  }, [projectId]);

  const {
    data: activityTypeListData,
    isFetching: isActivityTypePending,
    isError: isActivityTypeError,
    refetchWithParams: getActivityList,
  } = useGetActivityTypesListByProjectId();

  const {
    data: topicListData,
    isFetching: isTopicPending,
    isError: isTopicError,
    isSuccess: isTopicSuccess,
    refetchWithParams: getTopicList,
    clearListOfTopicData,
  } = useGetTopicListByProjectId();

  const {
    mutateAsync: saveActivityApi,
    isPending: isSaveActivityPending,
    isError: isSaveActivityError,
    isSuccess: isSaveActivitySuccess,
  } = useSaveActivityForProjectMutation();

  const {
    mutateAsync: updateActivityApi,
    isPending: isUpdateActivityPending,
    isError: isUpdateActivityError,
    isSuccess: isUpdateActivitySuccess,
  } = useUpdateActivityForProjectMutation();

  useEffect(() => {
    if (isEditActivity && isNotEmpty(editActivityData) && !isEffectExecuted) {
      getTopicApi(getValueOrBlank(editActivityData?.activityTypeId));

      const topicIds = editActivityData?.topics?.map((topic) => topic.id) ?? [];

      reset({
        [AddProjectActivityFormKey.activityType]: getValueOrBlank(
          editActivityData?.activityTypeId,
        ),
        [AddProjectActivityFormKey.modality]: getValueOrBlank(
          editActivityData?.modality,
        ),
        [AddProjectActivityFormKey.topic]: topicIds,
        [AddProjectActivityFormKey.notes]: getValueOrBlank(
          editActivityData?.notes,
        ),
        [AddProjectActivityFormKey.actualDurationInMinutes]: getValueOrBlank(
          editActivityData?.actualDurationInMinutes,
        ),
        [AddProjectActivityFormKey.onBehalfOfCoachId]: getValueOrBlank(
          editActivityData?.onBehalfOfFacilitator?.id,
        ),
        [AddProjectActivityFormKey.activityDate]:
          dayjs(editActivityData?.activityDate).format(userCountryDateFormat) ??
          "",
      });

      if (isNotEmpty(topicListData)) {
        setIsEffectExecuted(true);
        topicRef.current?.resetValues(topicIds);
      }
    }
    if (!isEditActivity) {
      // current date as default for activity date
      setValue(
        AddProjectActivityFormKey.activityDate,
        dayjs().format(userCountryDateFormat),
      );
    }
  }, [isEditActivity, editActivityData, isTopicSuccess, topicListData]);

  useEffect(() => {
    if (isSaveActivitySuccess || isUpdateActivitySuccess) {
      const toastMessage =
        isUpdateActivitySuccess && isEditActivity
          ? getLabel(
              activityTimelineaddActivitySiteCoreKeys.activityUpdatedMessage,
            )
          : getLabel(
              activityTimelineaddActivitySiteCoreKeys.activityAddedMessage,
            );
      displayToast(toastMessage);
      handleCloseModal();
      getListOfActivitiesApi();
    }
  }, [isSaveActivitySuccess, isUpdateActivitySuccess]);

  const isLoading =
    isActivityTypePending ||
    isTopicPending ||
    isSaveActivityPending ||
    isUpdateActivityPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  const isError =
    isActivityTypeError ||
    isTopicError ||
    isSaveActivityError ||
    isUpdateActivityError;

  useEffect(() => {
    if (isError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isError]);

  const closeMainModalHandler = () => {
    if (isDirty) setShowCancel(true);
    else handleCloseModal();
  };

  const activityTitle = React.useMemo(() => {
    return () => {
      if (isEditActivity) {
        return getLabel(
          activityTimelineaddActivitySiteCoreKeys.editActivityPopupHeader,
        );
      }
      return fetchComponentTitle(
        activityTimelineaddActivitySiteCoreKeys.activityTimeline,
      );
    };
  }, [isEditActivity]);

  const onSubmitClick = () => {
    const {
      activityType,
      modality,
      topic,
      notes,
      actualDurationInMinutes,
      onBehalfOfCoachId,
      activityDate,
    } = getValues();

    const params: ProjectParticipantActivityParams = {
      activityTypeId: activityType,
      activityName:
        activityTypeListData?.find(
          (item: IdNameList) => item.id === activityType,
        )?.name ?? "",
      modalityKey: modality,
      topics: topic,
      notes,
      onBehalfOfFacilitatorId: onBehalfOfCoachId,
      actualDurationInMinutes,
    };

    if (isEditActivity) {
      if (editActivityData?.id) {
        updateActivityApi({
          params: {
            ...params,
            programId: projectId,
            activityDate: dirtyFields?.activityDate
              ? convertToUTCFormat(activityDate, userCountryDateFormat)
              : editActivityData.activityDate,
          },
          participantActivityTimeLineId: editActivityData?.id,
        });
      }
    } else {
      saveActivityApi({
        params: {
          ...params,
          actualDurationInMinutes,
          onBehalfOfFacilitatorId: onBehalfOfCoachId,
          activityDate: activityDate
            ? convertToUTCFormat(activityDate, userCountryDateFormat)
            : "",
        },
        projectId: projectId,
      });
    }
  };

  const onConfirmClick = () => {
    handleSubmit(onSubmitClick)();
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    displayToast(
      isEditActivity
        ? getLabel(
            activityTimelineaddActivitySiteCoreKeys.cancelConfirmationText,
          )
        : getLabel(
            activityTimelineaddActivitySiteCoreKeys.changesDiscardedText,
          ),
    );
    handleCloseModal();
  };

  const handleActivityTypeChange = (activityTypeKey: string) => {
    setIsEffectExecuted(true);
    resetModality();
    resetTopic();
  };

  const resetModality = () => {
    setFilteredModality([]);
    setValue(AddProjectActivityFormKey.modality, "");
  };

  const resetTopic = () => {
    clearListOfTopicData();
    setValue(AddProjectActivityFormKey.topic, []);
    topicRef.current?.resetValues([]);
  };

  const handleModalityChange = () => {
    setIsEffectExecuted(true);
    resetTopic();
    const activityTypeKey = getValues(AddProjectActivityFormKey.activityType);
    if (activityTypeKey) {
      getTopicApi(activityTypeKey);
    }
  };

  const getTopicApi = (activityTypeKey: string) => {
    getTopicList(activityTypeKey, projectId);
  };

  const activityType = useWatch({
    name: AddProjectActivityFormKey.activityType,
    control,
  });

  useEffect(() => {
    getModalityOptionsByActivityType();
  }, [activityType]);

  const getModalityOptionsByActivityType = () => {
    if (!isNotEmpty(activityTypeListData)) return [];

    const matched = activityTypeListData.filter(
      (item: ActivityTypeListItem) => item.id === activityType,
    );

    const modalityTopics = matched[0]?.modalities?.map((item: string) => ({
      label:
        fetchValueByKeyOfDropdown(
          activityTimelineaddActivitySiteCoreKeys.activityTimeline,
          activityTimelineaddActivitySiteCoreKeys.modalityList,
          item,
        ) ?? "",
      value: item ?? "",
    }));

    setFilteredModality(modalityTopics ?? []);
  };

  return (
    <MuiModal
      open={show}
      onClose={closeMainModalHandler}
      title={activityTitle()}
      primaryButtonTitle={
        isEditActivity ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.close}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={closeMainModalHandler}
      hasLargeContent
    >
      <AddActivityWrapper data-testid={`${pageId}-add-activity`}>
        <FormProvider {...methods}>
          <ActivityForm
            pageId={pageId}
            filteredActivityTypes={setApiDataToDropDownOption(
              activityTypeListData,
            )}
            filteredModality={filteredModality}
            topicListData={topicListData}
            programCoachList={projectCoachList ?? []}
            topicRef={topicRef}
            handleActivityTypeChange={handleActivityTypeChange}
            handleModalityChange={handleModalityChange}
            isBulkAdd={false}
            siteCoreKeys={activityTimelineaddActivitySiteCoreKeys}
          />
        </FormProvider>

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
            cancelMessage={getLabel(
              activityTimelineaddActivitySiteCoreKeys.closeConfirmationText,
            )}
          />
        )}
      </AddActivityWrapper>
    </MuiModal>
  );
};
